import type { FC, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import type { Control } from "react-hook-form";
import { Controller, useFormContext, useForm, useController, useWatch } from "react-hook-form";
import { Box, Stack, IconButton, Tooltip, RadioGroup, FormControl, FormControlLabel, FormLabel } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { Radio } from "@privacy/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TrashCanIcon, PencilIcon } from "../../shared/icons";
import type { InputProps } from "../DynamicInput";
import type { DocumentReference, FileReference, MultipleExternalLinkValue } from "../types";
import { getDocumentReferenceKey } from "../types";
import type { FileOwner } from "../../api/files";
import { downloadFileMutationOptions, isFileUploading } from "../../api/files";
import * as FormDialog from "../../shared/FormDialog";
import { ConfirmDeleteDialog } from "../../shared/ConfirmDeleteDialog";
import { DocumentLink, getDownloadErrorMessageKey } from "../../dynamicShared/DocumentLink";
import { ErrorMessage } from "../inlineForm/ErrorMessage";
import { hasErrorCode } from "../../api/errors";
import { useDynamicFormState } from "../DynamicFormProvider";
import { isAnswerRequestEmpty } from "./isAnswerRequestEmpty";
import { AddDialogTrigger } from "./AddDialogTrigger";
import { FilePicker, useIgnoreFileDropOutsideDropArea } from "./FilePicker";

export const MultipleExternalLinkInput: FC<InputProps<MultipleExternalLinkValue>> = ({
  questionCard,
  onSave,
  readonly,
}) => {
  const { t } = useTranslation();
  const { mandatoryQuestionErrorMessage, fileOwner } = useDynamicFormState();
  const [dialogState, setDialogState] = useState<
    | { type: "closed" }
    | { type: "edit"; reference: DocumentReference; index: number }
    | { type: "confirmFileDelete"; file: FileReference }
  >({ type: "closed" });

  const { control } = useFormContext();
  const saveMutation = useMutation({
    mutationFn: (value: MultipleExternalLinkValue) => onSave(value),
  });

  const { field } = useController({ control, name: questionCard.id });

  const fieldValue = (field.value ?? []) as MultipleExternalLinkValue;

  const saveDocuments = (newValue: MultipleExternalLinkValue) =>
    saveMutation.mutateAsync(newValue, { onSuccess: () => field.onChange(newValue) });

  const updateDocument = (index: number, value: DocumentReference) =>
    saveDocuments(fieldValue.map((reference, i) => (i === index ? value : reference)));

  const addDocument = (value: DocumentReference) => saveDocuments([...fieldValue, value]);

  const removeLink = (index: number) => {
    const newValue = fieldValue.filter((_, i) => i !== index);
    field.onChange(newValue);
    return saveMutation.mutateAsync(newValue);
  };

  const handleRemoveClick = (reference: DocumentReference, index: number) => {
    if (reference.kind === "file") {
      setDialogState({ type: "confirmFileDelete", file: reference });
      return;
    }
    removeLink(index);
  };

  return (
    <>
      <Stack sx={{ width: "100%", my: fieldValue.length > 0 ? 0.5 : 0 }}>
        {fieldValue.map((reference, index) => (
          <DocumentItem
            key={getDocumentReferenceKey(reference)}
            readonly={readonly}
            reference={reference}
            onRemoveClick={() => handleRemoveClick(reference, index)}
            onEditClick={() => setDialogState({ type: "edit", reference, index })}
          />
        ))}
        <Box>
          <AddDialogTrigger
            isMultistepFormVersion={false}
            isEmpty={fieldValue.length === 0}
            isReadOnly={readonly}
            renderDialog={props => (
              <DocumentDialog {...props} fileOwner={fileOwner} questionId={questionCard.id} onSubmit={addDocument} />
            )}
          >
            {t("Document.AddButton")}
          </AddDialogTrigger>

          {saveMutation.isError && <ErrorMessage />}
          {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(field.value) && (
            <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
          )}
        </Box>
      </Stack>

      {dialogState.type === "edit" && (
        <DocumentDialog
          fileOwner={fileOwner}
          questionId={questionCard.id}
          onClose={() => setDialogState({ type: "closed" })}
          onSubmit={reference => updateDocument(dialogState.index, reference)}
          defaultReference={dialogState.reference}
        />
      )}

      {dialogState.type === "confirmFileDelete" && (
        <ConfirmDeleteDialog
          isOpen
          onClose={() => setDialogState({ type: "closed" })}
          onDelete={() =>
            saveDocuments(
              fieldValue.filter(reference => reference.kind !== "file" || reference.fileId !== dialogState.file.fileId)
            )
          }
          headerText={t("Document.Delete.ConfirmHeader")}
          message={t("Document.Delete.ConfirmMessage", { fileName: dialogState.file.fileName })}
          confirmButtonLabel={t("Document.Delete.ConfirmButton")}
        />
      )}
    </>
  );
};

type DocumentItemProps = {
  reference: DocumentReference;
  onRemoveClick: () => void;
  onEditClick: () => void;
  readonly: boolean;
};

const DocumentItem: FC<DocumentItemProps> = ({ reference, onRemoveClick, onEditClick, readonly }) => {
  const { t } = useTranslation();
  const [isHovering, setIsHovering] = useState(false);

  const showEditable = !readonly && isHovering;
  const isFile = reference.kind === "file";
  const editLabel = isFile ? t("Document.Edit.Tooltip") : t("ExternalMultipleLink.EditLinkTitle.Tooltip");
  const removeLabel = isFile ? t("Document.Delete.Tooltip") : t("ExternalMultipleLink.DeleteLinkTitle.Tooltip");
  const isUrlLinkDisabled = readonly && !isFile;
  const downloadFileMutation = useMutation(downloadFileMutationOptions());

  return (
    <Box>
      <Box
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        sx={{
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: showEditable ? theme => theme.palette.action.hover : "revert",
          borderRadius: 1,
        }}
      >
        <DocumentLink
          reference={reference}
          downloadFileMutation={downloadFileMutation}
          sx={{ pointerEvents: isUrlLinkDisabled ? "none" : "auto" }}
        />
        <Stack direction="row" sx={{ gap: 0.5, visibility: showEditable ? "visible" : "hidden" }}>
          <Tooltip title={editLabel}>
            <IconButton aria-label={editLabel} onClick={onEditClick}>
              <PencilIcon size={14} />
            </IconButton>
          </Tooltip>
          <Tooltip title={removeLabel}>
            <IconButton aria-label={removeLabel} onClick={onRemoveClick}>
              <TrashCanIcon size={14} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      {downloadFileMutation.isPending && hasErrorCode(downloadFileMutation.failureReason, "FILE_SCAN_PENDING") && (
        <ErrorMessage variant="loading" errorMessage={t("Document.Card.ScanInProgress")} />
      )}
      {downloadFileMutation.isError && (
        <ErrorMessage errorMessage={t(getDownloadErrorMessageKey(downloadFileMutation.error))} />
      )}
    </Box>
  );
};

type DocumentFile = Pick<FileReference, "fileId" | "fileName" | "size">;

type DocumentFormValues = { label: string; kind: "link" | "file"; url: string; file: DocumentFile | null };

const toFormValues = (reference?: DocumentReference): DocumentFormValues =>
  reference?.kind === "file"
    ? { label: reference.label, kind: "file", url: "", file: reference }
    : { label: reference?.label ?? "", kind: "link", url: reference?.url ?? "", file: null };

const toDocumentReference = (
  { label, kind, url, file }: DocumentFormValues,
  savedFile?: FileReference
): DocumentReference => {
  const trimmedLabel = label.trim();
  if (kind === "link") return { label: trimmedLabel || url.trim(), url: url.trim() };
  if (!file) throw new Error("A file must be uploaded before the document is saved");

  const isLabelUnchangedFromSavedFile = savedFile !== undefined && trimmedLabel === savedFile.fileName;
  const shouldUseFileName = !trimmedLabel || isLabelUnchangedFromSavedFile;
  const { fileId, fileName, size } = file;
  const resolvedLabel = shouldUseFileName ? fileName : trimmedLabel;
  return { kind: "file", label: resolvedLabel, fileId, fileName, size };
};

type DocumentDialogProps = {
  onClose: () => void;
  onSubmit: (reference: DocumentReference) => Promise<unknown>;
  defaultReference?: DocumentReference;
  fileOwner: FileOwner | undefined;
  questionId: string;
};

const DocumentDialog: FC<DocumentDialogProps> = ({ onClose, onSubmit, defaultReference, fileOwner, questionId }) => {
  const { t } = useTranslation();
  const { handleSubmit, control, setFocus, setValue, getValues } = useForm<DocumentFormValues>({
    defaultValues: toFormValues(defaultReference),
  });
  const kind = useWatch({ control, name: "kind" });
  const [isConfirmingSwitchToLink, setIsConfirmingSwitchToLink] = useState(false);
  const savedFile = defaultReference?.kind === "file" ? defaultReference : undefined;
  const submitMutation = useMutation({ mutationFn: onSubmit });

  FormDialog.useAutoFocusField(setFocus, "label");
  useIgnoreFileDropOutsideDropArea();

  const handleKindChange = (value: DocumentFormValues["kind"]) => {
    if (value === "link" && savedFile) {
      setIsConfirmingSwitchToLink(true);
      return;
    }
    if (value === "file") {
      const label = getValues("label").trim();
      const isLabelSameAsUrl = label !== "" && label === getValues("url").trim();
      if (isLabelSameAsUrl) setValue("label", "");
    }
    setValue("kind", value, { shouldDirty: true });
  };

  const validateUrl = (url: string, values: DocumentFormValues) => {
    if (values.kind !== "link") return true;
    if (url.trim() === "") return t("Input.Validation.Required");
    return true;
  };

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Title>
        {defaultReference ? t("Document.Dialog.EditTitle") : t("Document.Dialog.AddTitle")}
      </FormDialog.Title>

      <FormDialog.Form
        onSubmit={handleSubmit(values =>
          submitMutation.mutate(toDocumentReference(values, savedFile), { onSuccess: onClose })
        )}
      >
        <FormDialog.Content>
          <FormDialog.FormError isError={submitMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="label"
              label={
                <>
                  {t("ExternalMultipleLink.Label")}{" "}
                  <Box component="span" sx={{ fontWeight: 400 }}>
                    {t("Document.Dialog.Optional")}
                  </Box>
                </>
              }
            />

            {fileOwner && <DocumentSourceField value={kind} onChange={handleKindChange} />}

            <AnimatedHeight>
              {kind === "link" && (
                <FormDialog.TextField
                  control={control}
                  name="url"
                  label={t("ExternalMultipleLink.Url")}
                  rules={{ validate: validateUrl }}
                  placeholder={t("ExternalMultipleLink.Placeholder")}
                  inputMode="url"
                  autoComplete="url"
                />
              )}
              {fileOwner && kind === "file" && (
                <DocumentFileField control={control} fileOwner={fileOwner} questionId={questionId} />
              )}
            </AnimatedHeight>
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={submitMutation.isPending}>
            {defaultReference ? t("ExternalMultipleLink.UpdateButton") : t("ExternalMultipleLink.AddButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
      {isConfirmingSwitchToLink && savedFile && (
        <ConfirmDeleteDialog
          isOpen
          onClose={() => setIsConfirmingSwitchToLink(false)}
          onDelete={async () => {
            if (getValues("label").trim() === savedFile.fileName) setValue("label", "");
            setValue("kind", "link");
          }}
          headerText={t("Document.SwitchToLink.ConfirmHeader")}
          message={t("Document.SwitchToLink.ConfirmMessage", { fileName: savedFile.fileName })}
          confirmButtonLabel={t("Document.SwitchToLink.ConfirmButton")}
        />
      )}
    </FormDialog.Dialog>
  );
};

type DocumentSourceFieldProps = {
  value: DocumentFormValues["kind"];
  onChange: (value: DocumentFormValues["kind"]) => void;
};

const DocumentSourceField: FC<DocumentSourceFieldProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const options = [
    { value: "link", label: t("Document.Source.Link") },
    { value: "file", label: t("Document.Source.File") },
  ] as const;

  return (
    <FormControl component="fieldset">
      <FormLabel
        component="legend"
        sx={theme => ({
          "fontSize": theme.typography.pxToRem(14),
          "lineHeight": 1.5,
          "pb": 1,
          "fontWeight": "600",
          "color": theme.palette.text.primary,
          "&.Mui-focused": { color: theme.palette.text.primary },
        })}
      >
        {t("Document.Source.Label")}
      </FormLabel>
      <RadioGroup row value={value} onChange={(_, kind) => onChange(kind === "file" ? "file" : "link")}>
        {options.map(option => (
          <FormControlLabel
            key={option.value}
            slotProps={{ typography: { sx: { fontSize: theme => theme.typography.pxToRem(14) } } }}
            value={option.value}
            control={<Radio size="small" sx={{ py: 1, px: 2 }} />}
            label={option.label}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

type DocumentFileFieldProps = {
  control: Control<DocumentFormValues>;
  fileOwner: FileOwner;
  questionId: string;
};

const DocumentFileField: FC<DocumentFileFieldProps> = ({ control, fileOwner, questionId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const validateFile = (file: DocumentFile | null, values: DocumentFormValues) => {
    if (values.kind !== "file") return true;
    if (isFileUploading(queryClient)) return t("Document.Error.StillUploading");
    if (!file) return t("Document.Error.FileRequired");
    return true;
  };

  return (
    <Controller
      control={control}
      name="file"
      rules={{ validate: validateFile }}
      render={({ field, fieldState }) => (
        <FilePicker
          label={t("Document.Source.File")}
          fileOwner={fileOwner}
          questionId={questionId}
          file={field.value}
          validationError={fieldState.error?.message}
          onChange={({ id: fileId, fileName, size }) => field.onChange({ fileId, fileName, size })}
        />
      )}
    />
  );
};

const AnimatedHeight: FC<{ children: ReactNode }> = ({ children }) => {
  const [height, setHeight] = useState<number>();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef.current) return;
    const observer = new ResizeObserver(([entry]) => setHeight(entry.borderBoxSize[0].blockSize));
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      sx={{
        height,
        "overflow": "hidden",
        "transition": "height 250ms ease-out",
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      <Box ref={contentRef}>{children}</Box>
    </Box>
  );
};
